/* eslint-disable consistent-return */
import isNil from 'ramda/src/isNil'
import filter from 'ramda/src/filter'
import length from 'ramda/src/length'
import gt from 'ramda/src/gt'
import prop from 'ramda/src/prop'
import path from 'ramda/src/path'
import pathOr from 'ramda/src/pathOr'
import propOr from 'ramda/src/propOr'
import validateForm from 'root/src/client/logic/form/util/validateForm'

import submitForm from 'root/src/client/logic/form/actions/submitForm'
import submitFormError from 'root/src/client/logic/form/actions/submitFormError'
import submitFormComplete from 'root/src/client/logic/form/actions/submitFormComplete'
import moduleIdFromKey from 'root/src/client/logic/route/util/moduleIdFromKey'

import setFormErrors from 'root/src/client/logic/form/actions/setFormErrors'
import clearForm from 'root/src/client/logic/form/thunks/clearForm'
import clearPartialFormKeys from 'root/src/client/logic/form/actions/clearPartialFormKeys'

import formSubmits from 'root/src/shared/descriptions/formSubmits'
import postFormSubmits from 'root/src/shared/descriptions/postFormSubmits'

import userIdFromPartialEntries from 'root/src/client/logic/form/selectors/userIdFromPartialEntries'
import generalRecordModification from 'root/src/client/logic/api/actions/generalRecordModification'

import invokeApiRequest from 'root/src/client/logic/api/util/invokeApiRequest'
import { CLEAR_PARTIAL_FORM_KEYS } from 'root/src/shared/descriptions/endpoints/endpointIds'

import { idProp } from 'root/src/client/logic/api/lenses'

import moduleDescriptions from 'root/src/shared/descriptions/modules'

import { formStoreLenses } from 'root/src/client/logic/form/lenses'
import stepIdSelector from 'root/src/client/logic/form/selectors/stepIdSelector'
import onSuccessRedirectSelectorSelector from 'root/src/client/logic/route/selectors/onSuccessRedirectSelectorSelector'
import setShareUrl from 'root/src/client/logic/static/actions/setShareUrl'
import propEq from 'ramda/src/propEq'
import orEmpty from 'root/src/shared/util/orEmpty'
import apiRequest from 'root/src/client/logic/api/thunks/apiRequest'
import submitFormDefaultOnSuccess	from 'root/src/client/logic/form/actions/submitFormDefaultOnSuccess'
import getCursorePosition from 'root/src/client/util/getCursorePosition'
import setVisibleSuccessMessage from 'root/src/client/logic/app/actions/setVisibleSuccessMessage'
import pushRouteClient from 'root/src/client/logic/route/util/pushRouteClient'

const { viewFormChild } = formStoreLenses

const defaultAction = endpointId => payload => apiRequest(endpointId, payload)

export const submitFormHof = (
	submitFormFn, moduleDescriptionsObj, validateFormFn, setFormErrorsFn,
	clearFormFn, submitFormCompleteFn, formSubmitsObj, postFormSubmitsObj, submitFormErrorFn,
) => (
	moduleKey, submitIndex, stepFormNotLast, isPostFormSubmit, e,
) => async (dispatch, getState) => {
	const nullSubmitIndex = isNil(submitIndex)
	const moduleId = moduleIdFromKey(moduleKey)

	const submits = prop(moduleId, isPostFormSubmit ? postFormSubmitsObj : formSubmitsObj)
	const multipleSubmits = gt(length(submits), 1)
	if (multipleSubmits && nullSubmitIndex && !stepFormNotLast) {
		return Promise.resolve()
	}

	const correctedSubmitIndex = nullSubmitIndex ? 0 : submitIndex
	// clear form error
	dispatch(submitFormErrorFn(moduleKey, submitIndex, { error: undefined }))
	dispatch(submitFormFn(moduleKey, submitIndex))
	const state = getState()
	const { previousRoute } = state

	try {
		const stepId = stepIdSelector(state, { moduleKey })
		const {	onSuccessRecordUpdates, endpointId } = pathOr({}, [
			moduleId, ...orEmpty(stepId, ['steps', stepId]), 'submits', correctedSubmitIndex,
		], moduleDescriptionsObj)
		const submit = (
			stepId
				? prop(correctedSubmitIndex, filter(propEq('stepId', stepId), submits))
				: prop(correctedSubmitIndex, submits)
		)
			|| prop(correctedSubmitIndex, submits)
		const noValidate = prop('noValidate', submit)

		const formData = noValidate
			? {}
			: await validateFormFn(moduleKey, state, submitIndex, isPostFormSubmit, stepId)

		if (stepFormNotLast) {
			return Promise.resolve()
		}

		const submitAction = propOr(defaultAction(endpointId), 'action', submit)
		const isDefault = !prop('action', submit)
		// eslint-disable-next-line consistent-return
		try {
			const res = await dispatch(
				submitAction(formData, { moduleId, moduleKey, previousRoute }, e),
			)
			if (prop('statusCode', res) >= 400) {
				dispatch(setFormErrorsFn(moduleKey, res))
				dispatch(submitFormErrorFn(moduleKey, submitIndex, res))
				return Promise.reject(res)
			}

			const successMessage = prop('successMessage', submit)
			if (successMessage) {
				const cursorPosition = getCursorePosition(e)
				dispatch(setVisibleSuccessMessage(true, cursorPosition, successMessage))
				setTimeout(() => {
					dispatch(setVisibleSuccessMessage(false, { x: 0, y: 0 }, ''))
				}, 5000)
			}
			const successPromises = []
			const onSuccessFn = prop('onSuccess', submit)
			const additionalThunkFn = prop('additionalThunk', submit)
			if (additionalThunkFn) {
				successPromises.push(dispatch(additionalThunkFn(formData, res)))
			}

			if (onSuccessFn) {
				const partialFormEntries = viewFormChild(`db-${moduleKey}`, state)
				if (partialFormEntries) {
					const partialKeys = Object.keys(partialFormEntries)
					const userId = userIdFromPartialEntries(partialFormEntries)

					await invokeApiRequest(
						CLEAR_PARTIAL_FORM_KEYS,
						{ userId, partialKeys },
						state,
					).then(() => {
						dispatch(clearPartialFormKeys(moduleKey))
						successPromises.push(dispatch(onSuccessFn(res, { moduleId, moduleKey })))
					})
				} else {
					successPromises.push(dispatch(onSuccessFn(res, { moduleId, moduleKey })))
				}
			}

			const recordId = idProp(res)
			const substitutes = { formData, recordStoreKey: recordId, res }
			if (onSuccessRecordUpdates) {
				successPromises.push(
					dispatch(generalRecordModification(
						substitutes,
						onSuccessRecordUpdates,
					)),
				)
			}

			dispatch(setShareUrl(path(['body', 'shareUrl'], res)))

			const onSuccessRedirect = onSuccessRedirectSelectorSelector(state, {
				submitIndex, moduleId, moduleKey, ...formData, ...res,
			})

			await Promise.all(successPromises)

			if (isDefault) {
				dispatch(submitFormDefaultOnSuccess({
					payload: res.body,
					moduleKey,
				}))
			}
			dispatch(submitFormCompleteFn(moduleKey))

			if (onSuccessRedirect) {
				const { routeId, routeParams, query } = onSuccessRedirect
				await pushRouteClient({
					routeId,
					routeParams,
					query,
				})
			}
			dispatch(clearFormFn({}, { moduleKey, moduleId }))
			dispatch(submitFormError(moduleKey, submitIndex, { error: undefined }))
		} catch (errors) {
			console.error(errors)
			dispatch(setFormErrorsFn(moduleKey, errors))
			dispatch(submitFormErrorFn(moduleKey, submitIndex, errors))
			dispatch(submitFormCompleteFn(moduleKey))
		}
	} catch (errors) {
		console.error(errors)
		dispatch(setFormErrorsFn(moduleKey, errors))
		dispatch(submitFormErrorFn(moduleKey, submitIndex, errors))
		dispatch(submitFormCompleteFn(moduleKey))
	}
}

export default submitFormHof(
	submitForm, moduleDescriptions, validateForm, setFormErrors, clearForm,
	submitFormComplete, formSubmits, postFormSubmits, submitFormError,
)
